import React, {
  useEffect, useState, Suspense, useMemo,
} from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Loader, Button } from 'semantic-ui-react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import i18n from 'src/i18n';

import styled, { ThemeProvider } from 'styled-components';
import api from 'api';
import actions from 'actions';
import util from 'utils/utils';
import websocketApi from 'api/websocket';
import useTheme from 'theme/useTheme';
import toast from 'react-hot-toast';

import AgreeToTerms from 'components/lib/Entry/AgreeToTerms';
import VerifyEmail from 'components/lib/Entry/VerifyEmail';
import InitialProfileSetup from 'components/lib/Entry/InitialProfileSetup';
import ProvideEmail from 'components/lib/Entry/ProvideEmail';
import InterestsChooser from 'components/lib/Choosers/Tags/InterestsChooser';
import MessageMoles from 'components/lib/Messaging/MessageMolesContainer';
import VideoChat from 'components/lib/VideoChat/VideoChat';

import CustomOnboarding from './lib/CustomOnboarding';
import EventSubscriptions from './EventSubscriptions';
import EdgeSuggestionModal from './lib/EdgeSuggestionModal';
import { MountPoint } from './lib/Modal/MountPoint';

import AppRouting from './app/AppRouting';
import Toaster from './app/Toaster';
import SuspenseLoading from './app/SuspenseLoading';
import { useIntercom } from './app/useIntercom';

import { serviceWorkerBridge } from '../serviceWorkerBridge';

window.onbeforeunload = () => {
  websocketApi.disconnect();
};

// Dashboard styles
export const StyledApp = styled.div`
  &,.ui, .ui :not(.icon), body, h1, h2, h3, h4, h5, span, input, p {
    font-family: 'Inter';
    -webkit-font-smoothing: antialiased;
  }
  .ui.container {
    display: flex;
    flex: 1;
    flex-direction: column;
  }
  .idea img {
    max-width: 100%;
  }
  height: 100%;

  ${({ theme }) => (theme ? `
    a,a:hover{color:${theme.primaryColour};}
    a.with-border{border-bottom:1px dotted ${theme.primaryColour};}
    .primary-text{color: ${theme.primaryColour};}
    .ui.button.primary:not(.basic){background-color:${util.adjustColour(theme.primaryColour, 0.1)} !important;}
    .ui.button.primary.basic,.ui.button.primary.basic:hover{background:none !important; box-shadow:none !important;border:1px solid ${util.adjustColour(theme.primaryColour, 0.3)} !important;color:${util.adjustColour(theme.primaryColour, 0.3)} !Important;}
    .ui.button.secondary:not(.basic){background-color:${util.adjustColour(theme.secondaryColour, 0.1)} !important;}
    .ui.button.secondary.basic,.ui.button.secondary.basic:hover{background:none !important; box-shadow:none !important;border:1px solid ${util.adjustColour(theme.secondaryColour, 0.3)} !important;color:${util.adjustColour(theme.secondaryColour, 0.3)} !important;}
  ` : '')}
`;

function App({
  user, urlOrganisation, selectedMessageThreads, onReceiveOrganisation, onUpdateAvailableOrganisations, history, location, onReceiveUTMChallenge, onReceiveUTMLink, clearUtm, setClearUtm,
}) {
  const organisation = user?.organisation || urlOrganisation;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const params = useMemo(() => new URLSearchParams(window.location.search), [window.location.search]);
  const lang = params.get('lang') || organisation?.defaultLanguage;
  const utmLinkReferrer = params.get('utm-link');
  const utmChallengeReferrer = params.get('utm-challenge');
  const userId = user?._id;
  const [resumingSession, setResumingSession] = useState(true);
  const [showingTagChooser, setShowingTagChooser] = useState(null);
  const theme = useTheme();
  const appName = useMemo(() => organisation?.appName || 'Simply Do', [organisation?.appName]);
  const [promptReload, setPromptReload] = useState(true);

  /*
    Custom intercom hook to boot intercom with relevant user information
  */
  useIntercom(user, history);

  useEffect(() => {
    setResumingSession(true);
    api.auth.resumeSession(() => setResumingSession(false), () => setResumingSession(false));
  }, []);

  useEffect(() => {
    serviceWorkerBridge.navigator = history;
  }, [history]);

  useEffect(() => {
    if (clearUtm) {
      // Clear utm's after a referral has been tracked
      // Used to clear utms after login, resume session and register
      params.delete('utm-challenge');
      params.delete('utm-link');
      history.push({ search: params.toString() });
      setClearUtm(false);
    }
  }, [params, history, setClearUtm, clearUtm]);

  useEffect(() => {
    if (showingTagChooser === null && user && user?.organisation) {
      setShowingTagChooser(util.shouldShowInterestsChooser(user));
    }
  }, [showingTagChooser, user]);

  useEffect(() => {
    const code = util.code();
    if (code) {
      api.organisations.getByCode(code, (newOrganisation) => {
        onReceiveOrganisation(newOrganisation);
      });
    }
  }, [onReceiveOrganisation]);

  useEffect(() => {
    if (lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  /*
    UTM is a value associated with a custom URL. We attach these to our sdi.click links, to allow tracking analytics. We can then attribute sign-ups, clicks and views to specific sdi.click links and track timing
  */
  useEffect(() => {
    if (utmLinkReferrer) onReceiveUTMLink(utmLinkReferrer);
  }, [utmLinkReferrer, onReceiveUTMLink]);

  // Check for new version of the app, prompt the user to refresh if one is available
  useEffect(() => {
    if (api?.webVersion && import.meta.env.VITE_STAGE !== 'Dev' && import.meta.env.VITE_GIT_SHA !== api.webVersion && promptReload) {
      setPromptReload(false);
      toast(
        <div>
          <p style={{ fontWeight: 'bold', marginBottom: 5 }}>A new version of SimplyDo is available</p>
          <p style={{ fontSize: 17 }}>To load the most recent version simply refresh the page. Please make sure you have do not have any unsaved changes.</p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button primary icon='refresh' onClick={() => window.location.reload()} content='Refresh' />
          </div>
        </div>, { duration: 15000, style: { minWidth: '600px' } },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptReload, api.webVersion]);

  useEffect(() => {
    if (utmChallengeReferrer) onReceiveUTMChallenge(utmChallengeReferrer);
  }, [utmChallengeReferrer, onReceiveUTMChallenge]);

  /*
    Track page views when the page changes, and scroll to top if moving to challenge/idea pages
  */
  useEffect(() => {
    const regExr = new RegExp(/\/challenges|ideas\/([A-z0-9]+)\w+/g);
    if (!(regExr.test(window.location.pathname))) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (userId) {
      api.users.getOrganisations(userId, (orgs) => {
        onUpdateAvailableOrganisations(orgs);
      });
    }
  }, [userId, onUpdateAvailableOrganisations]);

  if (resumingSession) return <Loader active inline='centered' style={{ marginTop: 30 }} />;
  if (user && !util.hasAgreedTerms(user)) return <AgreeToTerms />;
  if (user && (!user.emails || !user.emails.length)) return <ProvideEmail />;
  if (user && !util.hasVerifiedEmail(user, user.organisation) && window.location.pathname !== '/verify-email') return <VerifyEmail />;
  if (user && !(user.profile?.firstName && user.profile?.lastName)) return <InitialProfileSetup />;
  if (user && !user.hasCompletedOrganisationOnboarding) return <CustomOnboarding />;
  if (showingTagChooser) return <InterestsChooser setVisible={setShowingTagChooser} />;
  return (
    <ThemeProvider theme={theme}>
      <StyledApp theme={theme}>
        <DndProvider backend={HTML5Backend}>
          <EventSubscriptions />
          <EdgeSuggestionModal />
          <Helmet defaultTitle={appName} titleTemplate={`%s | ${appName}`}>
            {import.meta.env.VITE_CSP
              && <meta http-equiv="Content-Security-Policy" content={`${import.meta.env.VITE_CSP}`} />
            }
            <link rel="shortcut icon" type="image/png" href={util.orgFavicon(organisation)} />
          </Helmet>
          <Suspense fallback={<SuspenseLoading />}>
            <AppRouting />
          </Suspense>
          <MessageMoles selectedMessageThreads={selectedMessageThreads} />
          <VideoChat />
          <Toaster />
          <MountPoint />
        </DndProvider>
      </StyledApp>
    </ThemeProvider>
  );
}

const mapStateToProps = (state) => {
  const { user } = state;
  const { selectedMessageThreads, messageThreads } = state.messages;
  const { urlOrganisation } = state.organisations;
  const { clearUtm } = state.auth;
  return {
    user, selectedMessageThreads, messageThreads, urlOrganisation, clearUtm,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onReceiveOrganisation: (org) => dispatch(actions.organisations.receiveUrlOrganisation(org)),
  onUpdateAvailableOrganisations: (orgs) => dispatch(actions.user.updateAvailableOrganisations(orgs)),
  onReceiveUTMLink: (utm) => dispatch(actions.auth.setUtmLink(utm)),
  onReceiveUTMChallenge: (utm) => dispatch(actions.auth.setUtmChallenge(utm)),
  setClearUtm: (shouldClear) => dispatch(actions.auth.setClearUtm(shouldClear)),
  setTrayOpen: (open) => dispatch(actions.notifications.setTrayOpen(open)),
});

const AppContainer = withRouter(connect(
  mapStateToProps, mapDispatchToProps,
)(App));

export default AppContainer;
